import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { AppWrapper, Block, Feedback, Spinner } from 'components';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import AuthProtected from 'containers/AuthProtected/AuthProtected';
import { actions as blogActions } from 'coupon-common/src/modules/admin/blogManager';

import styles from './AuthorProfiles.module.scss';
import messages from './messages';

const getInitialForm = () => ({
  user: '',
  slug: '',
  display_name: '',
  headline: '',
  bio: '',
  profile_image: '',
  structured_data_json: '{}',
  job_title: '',
  organization: '',
  expertise_tags: '',
  years_experience: 0,
  credentials: '[]',
  location_city: '',
  location_country: '',
  website_url: '',
  linkedin_url: '',
  topics_covered: '',
});

const formFromAuthor = author => ({
  user: author.user != null ? String(author.user) : '',
  slug: author.slug || '',
  display_name: author.display_name || '',
  headline: author.headline || '',
  bio: author.bio || '',
  profile_image: author.profile_image || '',
  structured_data_json: JSON.stringify(author.structured_data_json || {}, null, 2),
  job_title: author.job_title || '',
  organization: author.organization || '',
  expertise_tags: (author.expertise_tags || []).join(', '),
  years_experience: author.years_experience || 0,
  credentials: JSON.stringify(author.credentials || [], null, 2),
  location_city: author.location_city || '',
  location_country: author.location_country || '',
  website_url: author.website_url || '',
  linkedin_url: author.linkedin_url || '',
  topics_covered: (author.topics_covered || []).join(', '),
});

const AuthorProfiles = ({ intl, dispatch, authors, authorSave, authorDelete }) => {
  const [form, setForm] = useState(getInitialForm());
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(blogActions.getBlogAuthors());
  }, [dispatch]);

  const authorsList = authors && authors.data && authors.data.results
    ? authors.data.results
    : authors.data || [];

  const handleChange = event => {
    const { name, value } = event.target;

    setForm(prevForm => ({
      ...prevForm,
      [name]: name === 'years_experience' ? Number(value) : value,
    }));
  };

  const parseList = value =>
    (value || '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

  const buildPayload = () => {
    let structuredData = {};
    let credentials = [];

    try {
      structuredData = JSON.parse(form.structured_data_json || '{}');
    } catch (error) {
      structuredData = {};
    }

    try {
      credentials = JSON.parse(form.credentials || '[]');
    } catch (error) {
      credentials = [];
    }

    return {
      ...form,
      user: Number(form.user),
      years_experience: Number(form.years_experience || 0),
      structured_data_json: structuredData,
      expertise_tags: parseList(form.expertise_tags),
      topics_covered: parseList(form.topics_covered),
      credentials,
    };
  };

  const resetForm = () => {
    setForm(getInitialForm());
    setEditingId(null);
  };

  const handleSelectAuthor = author => {
    setForm(formFromAuthor(author));
    setEditingId(author.id);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleSave = () => {
    const userId = Number(form.user);

    if (!form.user || Number.isNaN(userId) || userId <= 0) {
      alert('Please enter a valid numeric User ID.');
      return;
    }

    if (!form.slug || !form.display_name) {
      alert('Slug and Display Name are required.');
      return;
    }

    if (editingId) {
      dispatch(blogActions.updateBlogAuthor(editingId, buildPayload()));
    } else {
      dispatch(blogActions.createBlogAuthor(buildPayload()));
    }
  };

  const handleDelete = (id, event) => {
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this author profile?')) {
      return;
    }

    dispatch(blogActions.deleteBlogAuthor(id));

    if (editingId === id) {
      resetForm();
    }
  };

  const isEditing = !!editingId;

  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText={intl.formatMessage(messages.pageHeadingText)}
    >
      <AuthProtected />

      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'Author Profiles']} />

        <div styleName="page-grid">
          <Block>
            <div styleName="form-header">
              <h4>{isEditing ? 'Edit Author Profile' : 'Create Author Profile'}</h4>
              <p>Add SEO/GEO-friendly author profile details.</p>
            </div>

            <div styleName="form">
              <div styleName="two-columns">
                <label>
                  User ID
                  <input name="user" value={form.user} onChange={handleChange} />
                </label>

                <label>
                  Slug
                  <input name="slug" value={form.slug} onChange={handleChange} placeholder="john-nick" />
                </label>
              </div>

              <div styleName="two-columns">
                <label>
                  Display Name
                  <input name="display_name" value={form.display_name} onChange={handleChange} />
                </label>

                <label>
                  Headline
                  <input name="headline" value={form.headline} onChange={handleChange} />
                </label>
              </div>

              <label>
                Bio
                <textarea name="bio" value={form.bio} onChange={handleChange} maxLength={500} rows={4} />
              </label>

              <div styleName="two-columns">
                <label>
                  Profile Image URL
                  <input name="profile_image" value={form.profile_image} onChange={handleChange} />
                </label>

                <label>
                  Job Title
                  <input name="job_title" value={form.job_title} onChange={handleChange} />
                </label>
              </div>

              <div styleName="two-columns">
                <label>
                  Organization
                  <input name="organization" value={form.organization} onChange={handleChange} />
                </label>

                <label>
                  Years Experience
                  <input type="number" min="0" name="years_experience" value={form.years_experience} onChange={handleChange} />
                </label>
              </div>

              <div styleName="two-columns">
                <label>
                  City
                  <input name="location_city" value={form.location_city} onChange={handleChange} />
                </label>

                <label>
                  Country
                  <input name="location_country" value={form.location_country} onChange={handleChange} />
                </label>
              </div>

              <div styleName="two-columns">
                <label>
                  Website URL
                  <input name="website_url" value={form.website_url} onChange={handleChange} />
                </label>

                <label>
                  LinkedIn URL
                  <input name="linkedin_url" value={form.linkedin_url} onChange={handleChange} />
                </label>
              </div>

              <label>
                Expertise Tags
                <input name="expertise_tags" value={form.expertise_tags} onChange={handleChange} placeholder="SEO, local deals, marketing" />
              </label>

              <label>
                Topics Covered
                <input name="topics_covered" value={form.topics_covered} onChange={handleChange} placeholder="Coupons, business growth, savings" />
              </label>

              <label>
                Credentials JSON
                <textarea name="credentials" value={form.credentials} onChange={handleChange} rows={4} />
              </label>

              <label>
                Structured Data JSON
                <textarea name="structured_data_json" value={form.structured_data_json} onChange={handleChange} rows={5} />
              </label>

              <div styleName="button-row">
                <button type="button" styleName="primary-button" onClick={handleSave}>
                  {isEditing ? 'Save Changes' : 'Save Author Profile'}
                </button>

                {isEditing && (
                  <button type="button" styleName="secondary-button" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                )}
              </div>

              <Feedback show={!!(authorSave && authorSave.error)} invalid content={authorSave && authorSave.error} />
              <Feedback show={!!(authorDelete && authorDelete.error)} invalid content={authorDelete && authorDelete.error} />
            </div>
          </Block>

          <Block>
            <div styleName="list-header">
              <h4>Existing Authors</h4>
              <p>Author profiles available for blogs.</p>
            </div>

            {authors && authors.fetching ? (
              <Spinner show />
            ) : (
              <div styleName="author-list">
                {authorsList.map(author => (
                  <div
                    key={author.id}
                    styleName={editingId === author.id ? 'author-card author-card-active' : 'author-card'}
                    onClick={() => handleSelectAuthor(author)}
                  >
                    <h5>{author.display_name}</h5>
                    <p>{author.headline}</p>
                    <span>{author.slug}</span>

                    <div styleName="author-card-actions">
                      <button type="button" styleName="link-button" onClick={() => handleSelectAuthor(author)}>
                        Edit
                      </button>
                      <button type="button" styleName="link-button" onClick={event => handleDelete(author.id, event)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Block>
        </div>
      </div>
    </AppWrapper>
  );
};

AuthorProfiles.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  authors: PropTypes.object,
  authorSave: PropTypes.object,
  authorDelete: PropTypes.object,
};

const mapStateToProps = state => ({
  authors: state.admin.blogManager.authors,
  authorSave: state.admin.blogManager.authorSave,
  authorDelete: state.admin.blogManager.authorDelete,
});

export default connect(mapStateToProps)(
  injectIntl(CSSModule(AuthorProfiles, styles, { allowMultiple: true })),
);